import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PageHeaderNew } from '@/components/ui/PageHeaderNew';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { EmptyState } from '@/components/ui/EmptyState';
import { Skeleton } from '@/components/ui/Skeleton';
import { AuthButton } from '@/core/permissions/AuthButton';
import { useAuth } from '@/core/auth/AuthContext';
import { useProcessTemplates } from '../hooks/useProcessTemplates';

const categoryLabels: Record<string, string> = {
  construction: 'Thi công',
  maintenance: 'Bảo dưỡng',
  lighting: 'Chiếu sáng',
  hardscape: 'Hardscape',
  other: 'Khác',
};

export function ProcessTemplatesListPage() {
  const navigate = useNavigate();
  const { profile } = useAuth();
  const [search, setSearch] = useState('');
  const [categoryCode, setCategoryCode] = useState('all');
  const filters = useMemo(() => ({ search, category_code: categoryCode }), [search, categoryCode]);
  const { templates, isLoading } = useProcessTemplates(profile?.company_id, filters);

  return (
    <div className="flex h-full flex-col bg-slate-50">
      <div className="shrink-0 p-4">
        <PageHeaderNew
          title="Quy trình mẫu công việc"
          subtitle="Tạo bộ quy trình như trồng cây bóng mát để áp nhanh đầu mục, tài nguyên và đơn giá vào dự toán"
          className="mb-4"
        />

        <div className="grid gap-3 lg:grid-cols-[1fr_220px_auto]">
          <input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Tìm mã hoặc tên quy trình"
            className="h-10 rounded-md border border-slate-200 bg-white px-3 sm:text-sm text-xs outline-none focus:border-emerald-500"
          />
          <select
            value={categoryCode}
            onChange={(event) => setCategoryCode(event.target.value)}
            className="h-10 rounded-md border border-slate-200 bg-white px-3 sm:text-sm text-xs outline-none focus:border-emerald-500"
          >
            <option value="all">Tất cả nhóm</option>
            <option value="construction">Thi công</option>
            <option value="maintenance">Bảo dưỡng</option>
            <option value="lighting">Chiếu sáng</option>
            <option value="hardscape">Hardscape</option>
            <option value="other">Khác</option>
          </select>
          <AuthButton module="estimates" action="create" onClick={() => navigate('/estimates/process-templates/new')}>
            <i className="ri-add-line mr-2" />
            Tạo quy trình
          </AuthButton>
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-auto px-4 pb-4">
        {isLoading ? (
          <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
            {Array.from({ length: 6 }).map((_, index) => (
              <Skeleton key={index} className="h-36 rounded-md" />
            ))}
          </div>
        ) : templates.length === 0 ? (
          <EmptyState
            icon="ri-node-tree"
            title="Chưa có quy trình mẫu"
            description="Tạo quy trình đầu tiên để áp nhanh vào hạng mục dự toán."
            action={<Button onClick={() => navigate('/estimates/process-templates/new')}>Tạo quy trình</Button>}
          />
        ) : (
          <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
            {templates.map((template) => (
              <Card key={template.id} className="border-slate-200 bg-white p-4 shadow-sm">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <Badge variant="info">{template.template_code}</Badge>
                      <Badge variant={template.status_code === 'active' ? 'success' : 'neutral'}>{template.status_code}</Badge>
                    </div>
                    <h3 className="mt-3 line-clamp-2 sm:text-sm text-xs font-medium text-slate-900">{template.template_name}</h3>
                    <p className="mt-1 line-clamp-2 sm:text-sm text-xs text-slate-500">{template.description || 'Chưa có mô tả'}</p>
                  </div>
                  <Button size="icon" variant="ghost" onClick={() => navigate(`/estimates/process-templates/${template.id}`)}>
                    <i className="ri-arrow-right-line" />
                  </Button>
                </div>
                <div className="mt-4 grid grid-cols-2 gap-2 sm:text-sm text-xs text-slate-600">
                  <Info label="Nhóm" value={categoryLabels[template.category_code] || template.category_code} />
                  <Info label="Đơn vị gốc" value={template.base_unit} />
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function Info({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md border border-slate-100 bg-slate-50 p-2">
      <p className="text-xs text-slate-500">{label}</p>
      <p className="mt-0.5 truncate sm:text-sm text-xs font-medium text-slate-800">{value || '-'}</p>
    </div>
  );
}
